import { config } from "../../../config/config";

export type CreateAffiliateInput = {
  firstname: string;
  lastname: string;
  emailid: string;
  /** Optional — affiliates do not receive admin dashboard login. */
  password?: string;
  phoneNumber?: string;
  referralcode?: string | null;
  referrallink?: string | null;
  aType: string;
  isVanderbiltNILFund: boolean;
  referredBy?: string | null;
  organization?: string;
};

const getNodeApiBaseUrl = () =>
  (
    process.env.REACT_APP_NODE_API_URL ||
    config.node_api_url ||
    (window.location.hostname === "localhost" ? "http://localhost:4000" : "")
  ).replace(/\/+$/, "");

export const getNodeAuthHeaders = (): Record<string, string> => {
  const idTokenKey = Object.keys(localStorage).find((key) => key.includes("idToken")) as string;
  const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : "";
  const hasuraRole = localStorage.getItem("x-hasura-user-role") || "";
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (idToken) headers.Authorization = `Bearer ${idToken}`;
  if (hasuraRole) headers["x-hasura-role"] = hasuraRole;
  return headers;
};

/**
 * Create affiliate via Node API (bypasses Hasura action schema).
 * Local Hasura metadata may lack password/phoneNumber on addAffiliate; the backend supports both.
 */
export const createAffiliateViaNodeApi = async (input: CreateAffiliateInput) => {
  const nodeApiBaseUrl = getNodeApiBaseUrl();
  if (!nodeApiBaseUrl) {
    throw new Error("REACT_APP_NODE_API_URL is not configured");
  }

  const controller = new AbortController();
  const timeoutMs = 120_000;
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${nodeApiBaseUrl}/api/addAffiliate`, {
      method: "POST",
      headers: getNodeAuthHeaders(),
      body: JSON.stringify({ input }),
      signal: controller.signal,
    });

    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(responseData?.message || `Request failed with status ${response.status}`);
    }

    return responseData as { message?: string };
  } catch (err: any) {
    if (err?.name === "AbortError") {
      throw new Error(
        "Creating affiliate timed out. The server may be overloaded (check Postgres/Hasura connections) or Cognito/Stripe is slow. Try again after restarting Docker services."
      );
    }
    throw err;
  } finally {
    window.clearTimeout(timeoutId);
  }
};
